'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { SceneImage } from '@/components/welcome/scene-image';
import { hasSiteScenery } from '@/lib/site-scenery';
import styles from './site-scenery.module.css';

/** One decorative frame shared by reader pages; Lumina owns its own appearance. */
export function SiteScenery({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const included = hasSiteScenery(pathname);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !included) return;

    // A comparison URL for reviewing the original appearance, without changing CMS data.
    const enabled = new URLSearchParams(window.location.search).get('scenery') !== 'off';
    root.dataset.scenery = enabled ? 'on' : 'off';
    if (!enabled) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(pointer: fine)');
    let frame = 0;
    let x = 0;
    let y = 0;
    let targetX = 0;
    let targetY = 0;
    const canMove = () => !reducedMotion.matches && finePointer.matches && !document.hidden;
    const paint = () => {
      x += (targetX - x) * 0.09;
      y += (targetY - y) * 0.09;
      root.style.setProperty('--forest-x', `${x.toFixed(2)}px`);
      root.style.setProperty('--forest-y', `${y.toFixed(2)}px`);
      frame = Math.abs(targetX - x) + Math.abs(targetY - y) > 0.05
        ? window.requestAnimationFrame(paint)
        : 0;
    };
    const reset = () => {
      window.cancelAnimationFrame(frame);
      frame = 0;
      x = y = targetX = targetY = 0;
      root.style.setProperty('--forest-x', '0px');
      root.style.setProperty('--forest-y', '0px');
    };
    const move = (event: PointerEvent) => {
      if (!canMove() || event.pointerType !== 'mouse') return;
      targetX = (event.clientX / window.innerWidth - 0.5) * 20;
      targetY = (event.clientY / window.innerHeight - 0.5) * 12;
      if (!frame) frame = window.requestAnimationFrame(paint);
    };

    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('blur', reset);
    document.documentElement.addEventListener('pointerleave', reset);
    document.addEventListener('visibilitychange', reset);
    reducedMotion.addEventListener('change', reset);
    finePointer.addEventListener('change', reset);
    return () => {
      reset();
      window.removeEventListener('pointermove', move);
      window.removeEventListener('blur', reset);
      document.documentElement.removeEventListener('pointerleave', reset);
      document.removeEventListener('visibilitychange', reset);
      reducedMotion.removeEventListener('change', reset);
      finePointer.removeEventListener('change', reset);
    };
  }, [pathname, included]);

  return (
    // Keep the route slot mounted when crossing into Lumina or the welcome scene.
    <div ref={rootRef} className={included ? styles.site : undefined} data-scenery={included ? 'on' : undefined}>
      {included ? <div className={styles.scenery} aria-hidden="true">
        <SceneImage src="/landing/assets/figma/left-full.png" alt="" className={styles.left} draggable={false} priority />
        <SceneImage src="/landing/assets/figma/right-full.png" alt="" className={styles.right} draggable={false} priority />
      </div> : null}
      {included ? <div className={styles.lighthouseScenery} aria-hidden="true">
        <SceneImage src="/landing/assets/untangled/lighthouse.png" alt="" className={styles.lighthouse} draggable={false} />
      </div> : null}
      {children}
    </div>
  );
}
